/**
 * UploadSourceBadge — shows "Uploaded by …" next to a candidate's name.
 *
 * The label depends on BOTH the uploader (stored on the application row)
 * AND the current viewer's identity:
 *
 *   uploaded by   | viewer is...          | shown as
 *   ─────────────────────────────────────────────────────────────
 *   super_admin   | (anyone)              | "Admin" (with shield icon)
 *   company_admin | the same employer     | "Myself"
 *   company_admin | a different employer  | "Company"
 *   company_admin | recruiter or admin    | "Company"
 *   recruiter     | the same recruiter    | "Myself"
 *   recruiter     | (anyone else)         | "Recruiter: <name>"
 *   self_apply    | (anyone)              | "Self-applied"
 *
 * Backend columns this reads (from /api/candidatelist etc):
 *   uploaded_by_email   string  — the uploader's email
 *   uploaded_by_type    string  — one of super_admin/company_admin/recruiter/self_apply
 *
 * Props:
 *   uploadedByEmail        string  (required for the badge to render)
 *   uploadedByType         string  (required for the badge to render)
 *   uploadedByCompanyName  string  (optional — recruiter's agency name;
 *                                   takes priority over email prefix
 *                                   for the recruiter chip label)
 *   currentViewerEmail     string  (optional — enables the "Myself" lookup)
 *   currentViewerType      string  (optional — same)
 *   variant                'compact' | 'standard' — sizing
 *   className              string  — pass-through for layout tweaks
 *
 * Renders nothing when both upload fields are missing — old rows from
 * before this column was added simply don't get a chip.
 */

import React from 'react';
import { Shield, User, Building2, UserCheck, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';

const TYPE_STYLES = {
  super_admin: {
    icon: Shield,
    cls:  'bg-purple-50 text-purple-700 border-purple-200',
    label: 'Admin',
  },
  company_admin: {
    icon: Building2,
    cls:  'bg-sky-50 text-sky-700 border-sky-200',
    label: 'Company',
  },
  recruiter: {
    icon: UserCheck,
    cls:  'bg-emerald-50 text-emerald-700 border-emerald-200',
    label: 'Recruiter',
  },
  self_apply: {
    icon: Mail,
    cls:  'bg-slate-50 text-slate-600 border-slate-200',
    label: 'Self-applied',
  },
  // Generic fallback (for any future type we forgot to register).
  unknown: {
    icon: User,
    cls:  'bg-slate-50 text-slate-600 border-slate-200',
    label: 'Uploaded',
  },
};

// Sizing presets — keep them in one place so the badge looks consistent
// across listings (Grid card vs Row vs CompanyAdmin table).
const VARIANT_CLS = {
  compact:  'text-[10px] px-1.5 py-0.5 gap-1',
  standard: 'text-xs   px-2   py-0.5 gap-1',
};

const ICON_SIZE = {
  compact:  'w-3 h-3',
  standard: 'w-3.5 h-3.5',
};


/**
 * Pretty-print an email for display in the badge.
 *
 * We keep it short for layout — `priya.sharma@example.com` becomes
 * `priya.sharma` (drop the domain), since recruiters scanning a list
 * don't care about the company suffix and dropping it saves precious
 * inline space in the dense candidate cards.
 */
const _shortName = (email) => {
  if (!email || typeof email !== 'string') return '';
  return email.split('@')[0].trim();
};


/**
 * Decide which label to show given (uploader, viewer) identity pair.
 * Pure function so it's trivially unit-testable.
 *
 * Returns { presetKey, label, tooltip }
 *   presetKey — keys into TYPE_STYLES (color / icon)
 *   label     — what shows on the chip
 *   tooltip   — full text on hover (uploader email + context)
 */
function _resolveBadge({
  uploadedByEmail, uploadedByType, uploadedByCompanyName,
  currentViewerEmail, currentViewerType,
}) {
  const preset = TYPE_STYLES[uploadedByType] || TYPE_STYLES.unknown;

  // Same-person check — case-insensitive, trimmed.
  const viewerNorm = (currentViewerEmail || '').trim().toLowerCase();
  const uploaderNorm = (uploadedByEmail || '').trim().toLowerCase();
  const isSelf = viewerNorm && uploaderNorm && viewerNorm === uploaderNorm;

  // Prefer the recruiter's agency / company name over their email
  // prefix when both are available — recruiters and hiring managers
  // care about the agency, not the individual.
  const companyName = (uploadedByCompanyName || '').trim();

  // Super-admin uploads always show as "Admin" regardless of viewer.
  // No "Myself" override because admins seeing their own upload as
  // "Admin" is still the most informative label.
  if (uploadedByType === 'super_admin') {
    return {
      presetKey: 'super_admin',
      label: 'Admin',
      tooltip: `Uploaded by  admin (${uploadedByEmail || 'unknown'})`,
    };
  }

  // Self-apply: candidate added themselves via the public job board.
  if (uploadedByType === 'self_apply') {
    return {
      presetKey: 'self_apply',
      label: 'Self-applied',
      tooltip: 'Candidate applied themselves via the public job board',
    };
  }

  // For company-admin / recruiter, show "Myself" when the viewer IS the
  // uploader. Otherwise show the role label (with the name when it's a
  // recruiter — recruiters often hand candidates over to the company,
  // so the company viewer needs to see WHICH recruiter sent them).
  if (isSelf) {
    return {
      presetKey: uploadedByType,
      label: 'Myself',
      tooltip: companyName
        ? `You uploaded this resume — ${companyName} (${uploadedByEmail})`
        : `You uploaded this resume (${uploadedByEmail})`,
    };
  }

  if (uploadedByType === 'recruiter') {
    // Priority: agency / company_name > email prefix > generic "Recruiter".
    // The agency name is what other actors care about — recruiters are
    // expected to hand candidates over, and the company wants to know
    // "which staffing firm sent this person", not the individual's name.
    const shown = companyName || _shortName(uploadedByEmail);
    return {
      presetKey: 'recruiter',
      label: shown ? `Recruiter: ${shown}` : 'Recruiter',
      tooltip: companyName
        ? `Uploaded by ${uploadedByEmail || 'recruiter'} (${companyName})`
        : `Uploaded by recruiter ${uploadedByEmail || 'unknown'}`,
    };
  }

  if (uploadedByType === 'company_admin') {
    return {
      presetKey: 'company_admin',
      label: 'Company',
      tooltip: `Uploaded by company admin (${uploadedByEmail || 'unknown'})`,
    };
  }

  // Fallback for unmapped types — show the email if we have one.
  return {
    presetKey: 'unknown',
    label: _shortName(uploadedByEmail) || 'Uploaded',
    tooltip: `Uploaded by ${uploadedByEmail || 'unknown source'}`,
  };
}


export default function UploadSourceBadge({
  uploadedByEmail,
  uploadedByType,
  uploadedByCompanyName,
  currentViewerEmail,
  currentViewerType,
  variant = 'standard',
  className = '',
}) {
  // No data → render nothing rather than a confusing empty chip.
  if (!uploadedByEmail && !uploadedByType) return null;

  const { presetKey, label, tooltip } = _resolveBadge({
    uploadedByEmail, uploadedByType, uploadedByCompanyName,
    currentViewerEmail, currentViewerType,
  });

  const preset = TYPE_STYLES[presetKey] || TYPE_STYLES.unknown;
  const Icon = preset.icon;

  return (
    <span
      className={cn(
        'inline-flex items-center font-medium border rounded whitespace-nowrap select-none',
        preset.cls,
        VARIANT_CLS[variant] || VARIANT_CLS.standard,
        className,
      )}
      title={tooltip}
    >
      <Icon className={ICON_SIZE[variant] || ICON_SIZE.standard} />
      {label}
    </span>
  );
}
